import { gql } from '@apollo/client';
import { all, call, put, select, spawn, takeLatest } from 'redux-saga/effects';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';

import { Query } from '@mfe/shared/schema-types';

import { AlertType, removeAlert } from '../alerts';
import { selectUser } from '../auth';
import { FetchWithErrorsMutation, graphqlMutationWithErrors, graphqlQuery } from '../graphql';
import { selectLocale } from '../locale';
import {
  getBuyMoreOffers as getBuyMoreOffersAction,
  purchaseBuyMore as purchaseBuyMoreAction,
  setBuyMoreOffers,
  setOffersError,
  setPurchaseError,
  setPurchaseSuccessful,
} from './buyMoreSlice';
import { waitForToken } from '../utils/utilsSagas';

const PURCHASE_BUYMORE = gql`
  mutation purchaseBuyMore($input: PurchaseBuyMoreInput!) {
    purchaseBuyMore(input: $input) {
      orderId
    }
  }
`;

const GET_BUY_MORE = gql`
  query getBuyMore($productInstanceId: String!, $languageCode: String!) {
    getBuyMore(input: { productInstanceId: $productInstanceId, languageCode: $languageCode }) {
      id
      amount
      description
      price
      disclosure
      savingsText
    }
  }
`;

function* getBuyMoreOffers() {
  yield call(waitForToken);
  const {
    user: { productInstanceId },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  const {
    locale: { displayLanguage },
  }: ReturnType<typeof selectLocale> = yield select(selectLocale);

  try {
    const data: Query = yield call(graphqlQuery, {
      query: GET_BUY_MORE,
      variables: { productInstanceId, languageCode: displayLanguage },
      fetchPolicy: 'no-cache',
    });

    if (!data?.getBuyMore) {
      yield put(setOffersError('Error fetching offers'));
      return;
    }

    yield put(setBuyMoreOffers(data?.getBuyMore as any));
  } catch (e) {
    yield put(setOffersError('Error fetching offers'));
  }
}

function* purchaseBuyMore({
  payload: { productTypeId, planName },
}: {
  type: string;
  payload: { productTypeId: string; planName?: string };
}) {
  try {
    const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
      mutation: PURCHASE_BUYMORE,
      variables: {
        input: { productTypeId },
      },
    });

    const { data, errors, runtimeError } = response;

    if (runtimeError || errors || !data?.purchaseBuyMore?.orderId) {
      yield put(setPurchaseError({ error: null, success: false }));

      pushToAdobeDataLayer({
        event: DataLayerEventName.FORM_SUBMIT,
        event_name: DataLayerEventName.FORM_SUBMIT,
        event_label: `${AnalyticsEventNames.BuyMoreOrderFailed} | DataBoost: ${planName}`,
        event_category: Categories.BuyMore,
      });

      return;
    }

    yield put(setPurchaseSuccessful());
    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_SUBMIT,
      event_name: DataLayerEventName.FORM_SUBMIT,
      event_label: `${AnalyticsEventNames.BuyMoreOrderSuccessful} | DataBoost: ${planName}`,
      event_category: Categories.BuyMore,
    });

    yield put(removeAlert(AlertType.AllDataUsed));
  } catch (e) {
    yield put(setPurchaseError({ error: 'Error purchasing buy more', success: null }));
  }
}

export function* watchBuyMore() {
  yield spawn(function* () {
    yield all([takeLatest(getBuyMoreOffersAction.type, getBuyMoreOffers)]);
    yield all([takeLatest(purchaseBuyMoreAction.type, purchaseBuyMore)]);
  });
}
